import {Component, Input, OnInit} from "@angular/core";
import {DialogComponent} from '../../../../../../ng2-bootstrap-modal/dialog.component';
import {DialogService} from '../../../../../../ng2-bootstrap-modal/dialog.service';
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {HostingService} from "../../hosting.service";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DbValidators} from "../../../../../shared/validators/DbValidators";
import {DbServerErrors} from "../../../../../shared/interceptors/db-server-errors";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../store';
import {Utils} from "../../../../../shared/utils/utils";
import {Config} from "../../../../../config/config";
import {
  IFcDropdownSelectOption
} from "../../../../../shared/components/form-controls/dropdown-select/fc-dropdown-select-option";
import {HostingDomainData} from "../../domains/hostingDomainData";
import {filter, first} from "rxjs/operators";

@Component({
    selector: 'db-remove-redirect-dialog',
    templateUrl: './add-redirect-dialog.component.html',
    styleUrls: ['./add-redirect-dialog.component.scss'],
    standalone: false
})
export class AddRedirectDialogComponent extends DialogComponent<null, Boolean> implements OnInit {

  @Input() websiteId: number;
  @Input() redirect: any | null;

  public loading: boolean;
  public changes: any;
  public formSubmitted: boolean;
  public addRedirectForm: FormGroup;
  public status: FormControl;
  public domains: FormControl;
  public serverErrors: DbServerErrors;
  public serverErrorsMessage: string;
  public hasServerFieldErrors: boolean;
  public statusOptions: IFcDropdownSelectOption[];
  public domainsOptions: IFcDropdownSelectOption[];
  public allDomains: HostingDomainData[];
  public isMobile: boolean;

  constructor(
    private _dialogService: DialogService,
    private _hostingService: HostingService,
    private _store: Store<fromStore.DbStoreState>
  ) {
    super(
      _dialogService
    );
  }

  ngOnInit() {
    if (window.innerWidth < 991) {
      this.isMobile = true;
    }
    const domainsFormatted = [];
    this.status = new FormControl(this.redirect ? this.redirect.statusCode.toString() : "301");
    this.domains = new FormControl("");
    this.statusOptions = [
      {
        label: "301(Permanent)",
        value: "301"
      },
      {
        label: "302(Temporary)",
        value: "302"
      }
    ];
    this.addRedirectForm = new FormGroup({
      'redirectFrom': new FormControl(this.redirect ? this.redirect.redirectFrom : '', [Validators.required, DbValidators.trim]),
      'redirectTo': new FormControl(this.redirect ? this.redirect.redirectTo : '', [Validators.required, DbValidators.trim]),
      'status': this.status,
      'domains': this.domains
    });


    this._store.select(fromStore.getDomains(this.websiteId)).subscribe((domains: HostingDomainData[]) => {
      if (domains.length && !this.allDomains) {
        this.allDomains = domains;
        // First add individual domains, excluding our subdomains
        domains
          .filter(domain => !Utils.checkStringInArray(domain.name, Config.hostnameList)) // Filter out our subdomains
          .map(domain => {
            domainsFormatted.push({
              "label": domain.name,
              "value": domain.id
            });
          });
        // Add "All Domains" at the end of the list only if we have any domains after filtering
        if (domainsFormatted.length > 0) {
          domainsFormatted.push({
            "label": "All Domains",
            "value": 0
          });
        }
        this.domainsOptions = domainsFormatted;

        if (this.redirect) {
          // Check if the currently selected domain is still available after filtering
          const isCurrentDomainAvailable = this.redirect.domainNameId === 0 || 
            domainsFormatted.some(d => d.value === this.redirect.domainNameId);
          
          this.domains.setValue(isCurrentDomainAvailable ? this.redirect.domainNameId : domainsFormatted[0].value);
        } else if (this.domainsOptions.length) {
          this.domains.setValue(this.domainsOptions[0].value);
        }
      }
    });

  }

  public cancel(): void {
    this.result = false;
    this.close();
  }

  public onFormSubmit() {
    this.formSubmitted = true;
    if (this.addRedirectForm.valid && this.addRedirectForm.controls['redirectFrom'].value && this.addRedirectForm.controls['redirectTo'].value && !this.isRedirectToHostname()) {
      this.loading = true;
      this.serverErrors = null;
      this.hasServerFieldErrors = null;
      if (this.redirect) {
        this._hostingService.updateRedirect(this.websiteId, this.domains.value, this.addRedirectForm.value, this.redirect.id)
          .pipe(
            filter((response: DbResponse): boolean => !!response),
            first())
          .subscribe((response: DbResponse): void => {
            this.loading = false;

            if (response.success) {
              this.close();
              this._store.dispatch(new fromStore.LoadRedirects(this.websiteId));
            } else {
              if (response.serverErrors) {
                this.checkHasServerFieldErrors(response.serverErrors);
                this.serverErrors = response.serverErrors;
              } else {
                this.serverErrorsMessage = response.message;
              }
            }

          });
      } else {
        this._hostingService.createRedirect(this.websiteId, this.domains.value, this.addRedirectForm.value)
          .pipe(filter((response: DbResponse): boolean => !!response),
            first())
          .subscribe((response: DbResponse): void => {
            this.loading = false;

            if (response.success) {
              this.close();
              this._store.dispatch(new fromStore.LoadRedirects(this.websiteId));
            } else {
              if (response.serverErrors) {
                this.checkHasServerFieldErrors(response.serverErrors);
                this.serverErrors = response.serverErrors;
              } else {
                this.serverErrorsMessage = response.message;
              }
            }

          });
      }
    }
  }

  public isRedirectToHostname(): boolean {
    const redirectToValue = this.addRedirectForm?.controls['redirectTo']?.value;
    if (!redirectToValue) {
      return false;
    }
    return Utils.checkStringInArray(redirectToValue, Config.hostnameList);
  }

  public isSubmitDisabled(): boolean {
    return this.loading || 
           !this.addRedirectForm?.valid || 
           !this.addRedirectForm?.controls['redirectFrom']?.value || 
           !this.addRedirectForm?.controls['redirectTo']?.value ||
           this.isRedirectToHostname();
  }

  private checkHasServerFieldErrors(serverErrors: DbServerErrors): void {
    this.hasServerFieldErrors = !!Utils.intersect(Object.keys(serverErrors.fields), ['redirectFrom']).length;
  }
}
